"use client"

import { useState } from "react"
import { ChevronRight, Link2, Check, Shield, Scale, CreditCard } from "lucide-react"
import Navbar from "@/app/Components/NavbarSection"
import Footer from "@/app/Sections/Footer"

const sections = [
    {
        id: "what-are-cookies",
        number: "01",
        title: "What Are Cookies",
        body: "Cookies are small text files that a website places on your device when you visit it. They allow the site to remember your actions and preferences (such as login, language, region, and display settings) over a period of time, so you do not have to keep re-entering them whenever you come back or browse from one page to another.",
    },
    {
        id: "why-we-use-cookies",
        number: "02",
        title: "Why We Use Cookies",
        body: "Aurex Hosting uses cookies and similar tracking technologies to operate our website, keep your account secure, remember your preferences, analyse how visitors interact with our pages, improve performance, and deliver relevant content. Cookies help us provide a faster, safer, and more personalised hosting experience.",
    },
    {
        id: "types-of-cookies",
        number: "03",
        title: "Types of Cookies We Use",
        body: "We use several categories of cookies. Strictly necessary cookies are required for core functions such as authentication, billing checkout, and security. Performance cookies help us understand how our pages perform so we can optimise loading speed and uptime. Functional cookies remember your choices like currency (LKR or USD) and dashboard layout. Analytics cookies, including Google Analytics, give us aggregated insight into traffic patterns. Marketing cookies may be set by advertising partners to measure campaign effectiveness.",
    },
    {
        id: "third-party-cookies",
        number: "04",
        title: "Third Party Cookies",
        body: "Some cookies on our site are placed by trusted third parties. These include payment processors used at checkout, live chat and helpdesk providers, content delivery and DDoS protection networks, and analytics platforms. These third parties operate under their own privacy and cookie policies, and we recommend reviewing them if you want to understand how they handle your data.",
    },
    {
        id: "cookie-duration",
        number: "05",
        title: "How Long Cookies Stay on Your Device",
        body: "Cookies on our site are either session cookies or persistent cookies. Session cookies are temporary and are removed automatically when you close your browser. Persistent cookies remain on your device for a set period (or until you delete them manually) and are used to remember preferences and recognise returning visitors across multiple sessions.",
    },
    {
        id: "managing-cookies",
        number: "06",
        title: "Managing Your Cookie Preferences",
        body: "You have full control over the cookies stored in your browser. Most browsers let you view, block, or delete cookies through their settings menu. You can choose to accept only necessary cookies or refuse all of them, but please note that disabling essential cookies may prevent parts of the Aurex Hosting platform (such as login or checkout) from working correctly.",
    },
    {
        id: "do-not-track",
        number: "07",
        title: "Do Not Track Signals",
        body: "Some browsers send a Do Not Track (DNT) request to websites. Because there is no widely accepted industry standard for how DNT signals should be handled, our site currently does not respond differently when a DNT signal is detected. You can still manage tracking by using the cookie controls in your browser.",
    },
    {
        id: "your-rights",
        number: "08",
        title: "Your Rights and Choices",
        body: "You may withdraw consent for non-essential cookies at any time by adjusting your browser settings or by clearing the cookies stored for our domain. Withdrawal will not affect the lawfulness of any processing carried out before your withdrawal, and it does not apply to strictly necessary cookies required to deliver the service you requested.",
    },
    {
        id: "changes",
        number: "09",
        title: "Changes to This Cookie Policy",
        body: "We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or the services we provide. Any updates will be posted on this page with a revised 'Last Updated' date. We encourage you to revisit this page periodically so you stay informed about how we use cookies.",
    },
    {
        id: "contact-us",
        number: "10",
        title: "Contact Us",
        body: "If you have any questions about our use of cookies or want to exercise your rights, please contact our support team via the helpdesk or write to us at our official email address. We will respond to your enquiry as soon as reasonably possible.",
    },
]

function CopyLinkButton({ id }: { id: string }) {
    const [copied, setCopied] = useState(false)
    const copy = () => {
        if (typeof window === "undefined") return
        navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${id}`)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <button
            onClick={copy}
            aria-label="Copy section link"
            className="flex-shrink-0 p-1.5 rounded-md text-gray-600 hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Link2 className="w-3.5 h-3.5" />}
        </button>
    )
}

export default function CookiePolicyPage() {
    return (
        <div className="bg-black">
            <Navbar />
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "url('https://cdn.aurex.lk/assets/hero-mc.webp')",
                            backgroundSize: "cover",
                            backgroundPosition: "center top",
                            filter: "blur(6px) brightness(0.45)",
                            transform: "scale(1.05)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#3b0d6e]/40" />
                    <div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                    />
                    <div
                        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
                        style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-24">
                        <nav className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 mb-8">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-gray-400">Legal</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-white">Cookie Policy</span>
                        </nav>
                        <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight tracking-wide mb-3">
                            Cookie Policy
                        </h1>
                        <p className="text-gray-500 font-poppins text-sm">Last updated: June 7, 2026</p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
                        <svg width="100%" height="50" viewBox="0 0 2598 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet" style={{ display: "block", position: "relative", zIndex: 20 }}>
                            <path d="M2598 150H0L230.359 0H2598V150Z" fill="black" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="relative z-10 w-full bg-black">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 relative">

                    <div className="flex flex-wrap gap-3 mb-10">
                        <a href="/privacy" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Shield className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Privacy Policy</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                        <a href="/tos" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Scale className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Terms of Service</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                        <a href="/refund-policy" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <CreditCard className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Refund Policy</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                    </div>

                    <p className="text-gray-400 font-poppins text-sm leading-relaxed mb-10 border-l-4 border-[#8B5CF6] pl-4">
                        This Cookie Policy explains how Aurex Hosting uses cookies and similar technologies when you visit our website or use our client dashboard. It describes what these technologies are, why we use them, and the choices you have to control them.
                    </p>

                    <div className="flex flex-col">
                        {sections.map((s, i) => (
                            <div key={s.id}>
                                <section id={s.id} className="py-8">
                                    <div className="flex items-start gap-3 mb-4">
                                        <span className="font-akira text-[#8B5CF6]/50 text-xs mt-1 flex-shrink-0 select-none">{s.number}</span>
                                        <h2 className="font-akira text-white text-base sm:text-lg leading-snug flex-1">{s.title}</h2>
                                        <CopyLinkButton id={s.id} />
                                    </div>
                                    <p className="text-gray-400 font-poppins text-sm leading-relaxed pl-7">{s.body}</p>
                                </section>
                                {i < sections.length - 1 && (
                                    <hr className="border-white/5" />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 pt-8 border-t border-white/5">
                        <p className="text-gray-600 font-poppins text-xs">
                            Questions about cookies?{" "}
                            <a href="mailto:cookie@aurex.lk" className="text-[#8B5CF6] hover:text-purple-400 transition-colors">
                                cookie@aurex.lk
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
